import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from "@devhop/ui";
import { useEffect, useMemo, useState } from "react";
import type {
  AssignmentDTO,
  CreateAssignmentDTO,
  EventKind,
  EventShowDTO,
  UpdateAssignmentDTO,
} from "../../domain/contracts";
import { isMusicArrangerShowBaseAmount } from "../../domain/contracts";
import { countBulkAssignmentPlan } from "../../domain/services/assignment-bulk-ui";
import { AssignmentForm } from "./AssignmentForm";
import { BulkAssignmentPreview } from "./BulkAssignmentPreview";
import {
  PerformerAssignmentBuilder,
  type PerformerAssignmentBuilderChange,
} from "./PerformerAssignmentBuilder";

export type EventTeamAssignDialogMode = "create" | "edit";

type EventTeamAssignDialogProps = {
  open: boolean;
  mode: EventTeamAssignDialogMode;
  eventKind: EventKind;
  /** Derived from event show schedule. */
  derivedShowCount?: number;
  eventShows?: EventShowDTO[];
  existingAssignments: AssignmentDTO[];
  editingAssignment?: AssignmentDTO | null;
  createSubmitting?: boolean;
  updateSubmitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (values: CreateAssignmentDTO) => void | Promise<void>;
  onUpdate: (values: UpdateAssignmentDTO) => void | Promise<void>;
  onApplyTemplates: (
    assignments: CreateAssignmentDTO[],
  ) => void | Promise<void>;
};

function dialogCopy(
  mode: EventTeamAssignDialogMode,
  performerName?: string | null,
): { title: string; description: string } {
  if (mode === "edit") {
    return {
      title: performerName ? `ແກ້ໄຂ: ${performerName}` : "ແກ້ໄຂຄ່າຕົວ",
      description: "ແກ້ໄຂບົດບາດ, ຈຳນວນໂຊ ແລະ ວັນຊ້ອມ",
    };
  }
  return {
    title: "ເພີ່ມນັກສະແດງ",
    description: "ເລືອກນັກສະແດງ ແລະ ກຳນົດຄ່າຕົວຕາມບົດບາດ",
  };
}

export function EventTeamAssignDialog({
  open,
  mode,
  eventKind,
  derivedShowCount,
  eventShows,
  existingAssignments,
  editingAssignment,
  createSubmitting,
  updateSubmitting,
  onOpenChange,
  onUpdate,
  onApplyTemplates,
}: EventTeamAssignDialogProps) {
  const [builderData, setBuilderData] =
    useState<PerformerAssignmentBuilderChange>({
      assignments: [],
      performerNames: new Map(),
    });
  const [builderKey, setBuilderKey] = useState(0);

  const { title, description } = dialogCopy(
    mode,
    editingAssignment?.performerName ?? editingAssignment?.performerId,
  );

  useEffect(() => {
    if (!open || mode !== "create") return;
    setBuilderData({ assignments: [], performerNames: new Map() });
    setBuilderKey((k) => k + 1);
  }, [open, mode]);

  const bulkPlan = useMemo(
    () => countBulkAssignmentPlan(existingAssignments, builderData.assignments),
    [existingAssignments, builderData.assignments],
  );

  const previewNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of existingAssignments) {
      if (row.performerName) map.set(row.performerId, row.performerName);
    }
    for (const [id, name] of builderData.performerNames) map.set(id, name);
    return map;
  }, [existingAssignments, builderData.performerNames]);

  const handleSubmit = async () => {
    if (bulkPlan.newCount === 0) {
      toast.error("ບໍ່ມີລາຍການໃໝ່ — ກວດການເລືອກ ຫຼື ບົດບາດ");
      return;
    }
    await onApplyTemplates(bulkPlan.toCreate);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,880px)] w-full max-w-6xl! flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {mode === "edit" && editingAssignment ? (
            <AssignmentForm
              mode="edit"
              eventKind={eventKind}
              derivedShowCount={derivedShowCount}
              eventShows={eventShows}
              initialValues={{
                performerId: editingAssignment.performerId,
                roleFeeKind: editingAssignment.roleFeeKind,
                showCount: editingAssignment.showCount,
                eventShowIds: editingAssignment.eventShowIds,
                rehearsalDays: editingAssignment.rehearsalDays,
                showBaseAmountOverride:
                  editingAssignment.roleFeeKind === "music_arranger" &&
                  isMusicArrangerShowBaseAmount(
                    editingAssignment.showBaseAmount,
                  )
                    ? editingAssignment.showBaseAmount
                    : undefined,
                lineKey: editingAssignment.lineKey,
                notes: editingAssignment.notes,
              }}
              submitting={updateSubmitting}
              onCancel={() => onOpenChange(false)}
              onSubmit={onUpdate}
            />
          ) : (
            <div className="space-y-4">
              <PerformerAssignmentBuilder
                key={builderKey}
                eventKind={eventKind}
                derivedShowCount={derivedShowCount}
                eventShows={eventShows}
                existingAssignments={existingAssignments}
                submitting={createSubmitting}
                onChange={setBuilderData}
              />
              <BulkAssignmentPreview
                performerNames={previewNames}
                planned={bulkPlan.toCreate}
                skipped={bulkPlan.skipped}
              />
            </div>
          )}
        </div>

        {mode === "create" && (
          <DialogFooter className="shrink-0 gap-2 border-t px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="button"
              disabled={createSubmitting || bulkPlan.newCount === 0}
              onClick={() => void handleSubmit()}
            >
              {createSubmitting
                ? "ກຳລັງບັນທຶກ..."
                : bulkPlan.newCount > 0
                  ? `ເພີ່ມ (${bulkPlan.newCount})`
                  : "ເພີ່ມ"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
